/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Locale } from "@i18n/react";
import { I18nMessage, useLocale, useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useIsBlockedFromManagingProfiles, useRequestMe } from "@packages/sdk";
import {
  Button,
  CloseIcon,
  createModal,
  Edit2Icon,
  IconButton,
  PlusIcon,
  showModal,
  Text,
  type ThemeType,
  useModal,
} from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { useKidProfiles } from "../hooks/useKidProfiles";
import { useModalResolution } from "../hooks/useModalResolution";
import type { UseSwitchProfilesProps } from "../hooks/useSwitchProfiles";
import { useSwitchProfiles } from "../hooks/useSwitchProfiles";
import { MAX_RELATED_ACCOUNTS_IS_KID } from "../lib/constants/maxRelatedAccountsIsKid";
import { ScreenNames } from "../lib/enums/screenNames";
import { Screens } from "../lib/enums/screens";
import { isValidKidsLocale } from "../lib/utils/isValidKidsLocale";
import { useContext } from "../Provider";
import { useRequestMeRelatedAccounts } from "../requests/useRequestMeRelatedAccounts";
import { Avatar } from "./blocks/Avatar";
import { FullscreenModal } from "./blocks/FullscreenModal";
import type { CreationProps } from "./Creation/Creation";
import { Creation } from "./Creation/Creation";
import { EnterPin, EnterPinType } from "./EnterPin";
import { ProfileReady } from "./ProfileReady";

export type SwitchProfilesProps = UseSwitchProfilesProps & {
  creationProps?: CreationProps;
  isShowProfileReady?: boolean;
};

const cssDivAddProfileContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
  justifyContent: "center",
  width: "100%",
  [`@media ${Screens.Large}`]: {
    gap: theme.spacing.l,
  },
});
const cssDivAvatarContainer: Interpolation<ThemeType> = (theme) => ({
  borderRadius: theme.radius.l,
  paddingBottom: theme.spacing.l,
  paddingLeft: theme.spacing.m,
  paddingRight: theme.spacing.m,
  paddingTop: theme.spacing.l,
});
const cssDivAvatarContainerRoleButton: Interpolation<ThemeType> = (theme) => ({
  cursor: "pointer",
  ":hover": {
    background: theme.semanticColors.neutralsLowest,
  },
});
const cssDivContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  maxWidth: 840,
  width: "100%",
});
const cssDivGrid: Interpolation<ThemeType> = (theme) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: theme.spacing.xs,
  [`@media ${Screens.Large}`]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
});
const cssDivHeaderContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
});
const cssMotionDivContent: Interpolation<ThemeType> = (theme) => ({
  overflowX: "hidden",
  overflowY: "auto",
  paddingBottom: theme.spacing.xxl,
});
const cssPlusIcon: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
});
const cssSpan: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.neutralsLowest,
  borderRadius: theme.radius.full,
  display: "flex",
  justifyContent: "center",
  overflow: "hidden",
  position: "relative",
  height: 88,
  width: 88,
  [`@media ${Screens.Large}`]: {
    height: 116,
    width: 116,
  },
});
const cssSpanTextContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
});
const cssTextLimit: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedHigh,
  marginTop: theme.spacing.l,
  textAlign: "center",
  [`@media ${Screens.Large}`]: {
    marginTop: theme.spacing.xxl,
  },
});
const cssTextMetadata: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const cssTextTitle: Interpolation = {
  margin: 0,
};
const cssActiveProfileIcon: Interpolation = {
  position: "absolute",
  right: 0,
  bottom: 0,
};
const cssDivAvatarContainerManage: Interpolation = {
  position: "relative",
};
const cssDivAvatarContainerClickable: Interpolation = {
  cursor: "pointer",
};
const cssAvatarManageScrim: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.shades.shade50,
  borderRadius: theme.radius.full,
  height: 88,
  left: "50%",
  pointerEvents: "none",
  position: "absolute",
  top: theme.spacing.l,
  transform: "translateX(-50%)",
  width: 88,
  zIndex: 1,
  [`@media ${Screens.Large}`]: {
    height: 116,
    width: 116,
  },
});
const cssEditOverlay: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.semanticColors.onOverlayBackground,
  display: "flex",
  height: 40,
  justifyContent: "center",
  left: "50%",
  pointerEvents: "none",
  position: "absolute",
  top: 68,
  transform: "translate(-50%, -50%)",
  width: 40,
  zIndex: 2,
  [`@media ${Screens.Large}`]: {
    top: 82,
  },
});

export const SwitchProfiles = createModal(
  ({
    creationProps = {},
    isShowProfileReady,
    onDismiss: onDismissProps,
    onSuccess: onSuccessProps,
  }: SwitchProfilesProps) => {
    const modal = useModal();
    const { onOpenChange, resolve } = useModalResolution({
      modal,
      onClose: onDismissProps,
    });
    const locale = useLocale();
    const t = useTranslations();
    const { setToastAfterRefresh } = useContext();

    const { track } = useAnalytics();
    const isLarge = useMediaQuery(Screens.Large);
    const {
      query: { data: me },
    } = useRequestMe();
    const { data: meRelatedAccounts } = useRequestMeRelatedAccounts();
    const kidProfiles = useKidProfiles() ?? [];
    const isBlockedFromManagingProfiles = useIsBlockedFromManagingProfiles();
    const [isManageMode, setIsManageMode] = useState(false);
    const [isShownProfileReady, setIsShownProfileReady] = useState(false);
    useViewedScreen({
      screen: ScreenNames.ProfileSelector,
    });

    const onSuccess = useCallback<UseSwitchProfilesProps["onSuccess"]>(
      (data) => {
        resolve(true, () => {
          setToastAfterRefresh(
            t("profile_switcher_toast_title", { 0: data.user.name }),
          );
          if (onSuccessProps) {
            onSuccessProps(data);
          } else {
            window.location.reload();
          }
          modal.hide();
        });
      },
      [modal.hide, onSuccessProps, resolve],
    );
    const { switchProfiles } = useSwitchProfiles({ onSuccess });

    const onClickManage = useCallback(
      (accountId: number, isKid: boolean) => {
        track({
          event: HallowAnalyticsEvent.TappedProfileSettings,
          properties: {
            screen_name: ScreenNames.ProfileSelector,
            target_account_type: isKid ? "kids" : "standard",
          },
        });
        if (accountId === me?.id) {
          window.location.assign("/settings/account");
        } else {
          window.location.assign(`/settings/kids/${accountId}`);
        }
      },
      [me?.id, track],
    );

    const onClickAddProfile = useCallback(() => {
      track({
        event: HallowAnalyticsEvent.TappedAddKidsProfile,
        properties: {
          screen_name: ScreenNames.ProfileSelector,
        },
      });
      showModal(Creation, {
        ...creationProps,
        onSuccess: (data) => {
          creationProps?.onSuccess?.(data);
          setIsShownProfileReady(true);
          showModal(ProfileReady);
        },
      });
    }, [creationProps]);
    const onClickProfile = useCallback(
      ({ isKid, userId }: { isKid: boolean; userId: number }) => {
        track({
          event: HallowAnalyticsEvent.TappedSwitchProfile,
          properties: {
            account_type: isKid ? "kids" : "standard",
            screen_name: ScreenNames.ProfileSelector,
          },
        });

        if (me?.is_kid)
          showModal(EnterPin, {
            onSuccess,
            type: EnterPinType.SwitchProfile,
            userId,
          });
        else switchProfiles({ userId });
      },
      [me?.is_kid, onSuccess, switchProfiles, track],
    );

    useEffect(() => {
      if (isShowProfileReady && !isShownProfileReady) {
        setIsShownProfileReady(true);
        showModal(ProfileReady);
      }
    }, [isShowProfileReady, isShownProfileReady]);

    return (
      <FullscreenModal
        contentCss={cssMotionDivContent}
        isOpen={modal.visible}
        onOpenChange={onOpenChange}
      >
        <div css={cssDivContainer}>
          <div css={cssDivHeaderContainer}>
            <RadixUIDialog.Close asChild>
              <IconButton
                icon={<CloseIcon size={20} />}
                variant="transparentPrimary"
              />
            </RadixUIDialog.Close>
            {!me?.is_kid && !isBlockedFromManagingProfiles && (
              <Button
                size="s"
                variant="neutral"
                onClick={() => {
                  if (!isManageMode) {
                    track({
                      event: HallowAnalyticsEvent.TappedManageProfiles,
                      properties: {
                        screen_name: ScreenNames.ProfileSelector,
                      },
                    });
                  }
                  setIsManageMode((prev) => !prev);
                }}
              >
                <I18nMessage
                  message={
                    isManageMode
                      ? "general_word_done"
                      : "profile_switcher_manage"
                  }
                />
              </Button>
            )}
          </div>
          <RadixUIDialog.Title asChild>
            <Text css={cssTextTitle} size="m" type="headline">
              <I18nMessage message="profile_switcher_title" />
            </Text>
          </RadixUIDialog.Title>
          <VisuallyHidden>
            <RadixUIDialog.Description>
              <I18nMessage message="profile_switcher_title" />
            </RadixUIDialog.Description>
          </VisuallyHidden>
          <div css={cssDivGrid}>
            {meRelatedAccounts?.map((account) => {
              const isRoleButton = account.id !== me?.id;

              return (
                <div
                  css={[
                    cssDivAvatarContainer,
                    (isRoleButton || isManageMode) &&
                      cssDivAvatarContainerRoleButton,
                    isManageMode && cssDivAvatarContainerManage,
                  ]}
                  key={account.id}
                  onClick={
                    isManageMode
                      ? (e) => {
                          e.stopPropagation();
                          onClickManage(account.id, account.is_kid);
                        }
                      : isRoleButton
                        ? () =>
                            onClickProfile({
                              isKid: account.is_kid,
                              userId: account.id,
                            })
                        : undefined
                  }
                  onKeyDown={
                    isManageMode
                      ? (event) => {
                          if (event.key === "Enter")
                            onClickManage(account.id, account.is_kid);
                        }
                      : isRoleButton
                        ? (event) => {
                            if (event.key === "Enter")
                              onClickProfile({
                                isKid: account.is_kid,
                                userId: account.id,
                              });
                          }
                        : undefined
                  }
                  role={isRoleButton || isManageMode ? "button" : undefined}
                  tabIndex={isRoleButton || isManageMode ? 0 : undefined}
                >
                  <Avatar
                    identifier={account.is_kid ? account.identifier : undefined}
                    selected={!isRoleButton && !isManageMode}
                    imgSrc={account.image_url}
                    metadata={account.is_kid ? undefined : account.identifier}
                    name={account.name || account.identifier || ""}
                  />
                  {isManageMode && (
                    <>
                      <span css={cssAvatarManageScrim} />
                      <span css={cssEditOverlay}>
                        <Edit2Icon size={40} />
                      </span>
                    </>
                  )}
                </div>
              );
            })}
            {!me?.is_kid &&
              !isBlockedFromManagingProfiles &&
              isValidKidsLocale(locale as Locale) &&
              kidProfiles.length < MAX_RELATED_ACCOUNTS_IS_KID && (
                <div
                  css={[
                    cssDivAvatarContainer,
                    cssDivAvatarContainerClickable,
                    cssDivAddProfileContainer,
                  ]}
                  onClick={onClickAddProfile}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onClickAddProfile();
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span css={cssSpan}>
                    <PlusIcon.Simple css={cssPlusIcon} size={34} />
                  </span>
                  <span css={cssSpanTextContainer}>
                    <Text
                      overflow="ellipsis"
                      size={isLarge ? "l" : "m"}
                      type="title"
                    >
                      <I18nMessage message="profile_switcher_add_kids_profile" />
                    </Text>
                    <Text
                      css={cssTextMetadata}
                      overflow="ellipsis"
                      size={isLarge ? "xl" : "l"}
                      type="detail"
                    >
                      <I18nMessage message="account_type_label_kids" />
                    </Text>
                  </span>
                </div>
              )}
          </div>
          {kidProfiles.length === MAX_RELATED_ACCOUNTS_IS_KID && (
            <Text css={cssTextLimit} size={isLarge ? "l" : "m"} type="body">
              <I18nMessage message="general_words_kids_profile_limit" />
            </Text>
          )}
        </div>
        {kidProfiles.length === MAX_RELATED_ACCOUNTS_IS_KID && (
          <Text css={cssTextLimit} size={isLarge ? "l" : "m"} type="body">
            <I18nMessage message="general_words_kids_profile_limit" />
          </Text>
        )}
      </FullscreenModal>
    );
  },
);
